import React, { useState, useEffect } from 'react';
import { PrizeTier, LeaderboardEntry } from './types';
import { getOrdinalSuffix } from './utils/helpers';
import { useAudio } from './hooks/useAudio';
import KBCUserProfile from './KBCUserProfile';
import { useMsal, useAccount } from '@azure/msal-react';
import { joinWaitlist } from '../../services/quizApi';
import { 
  copyShareLinkToClipboard, 
  openLinkedInShare,
  generateLinkedInMessage 
} from './utils/shareUtils';
import { FaLinkedin, FaLink, FaCheck } from 'react-icons/fa';

interface EndScreenProps {
  score: number;
  prize?: PrizeTier;
  totalQuestions: number;
  correctAnswers: number;
  cashedOut: boolean;
  walkedOut?: boolean;
  leaderboardPreview: LeaderboardEntry[];
  onViewFullLeaderboard: () => void;
  onBackToHome: () => void;
}

const EndScreen: React.FC<EndScreenProps> = ({
  score,
  prize,
  totalQuestions,
  correctAnswers,
  cashedOut,
  walkedOut = false,
  leaderboardPreview,
  onViewFullLeaderboard,
  onBackToHome,
}) => {
  const [waitlistSubmitted, setWaitlistSubmitted] = useState(false);
  const [waitlistLoading, setWaitlistLoading] = useState(false);
  const [waitlistError, setWaitlistError] = useState<string | null>(null);
  const [copySuccess, setCopySuccess] = useState(false);
  const { audioMuted } = useAudio();
  const { accounts } = useMsal();
  const account = useAccount(accounts[0] || {});
  
  const userEmail = account?.username || '';
  const displayName = account?.name || 'Guest User';

  // Share data for generating share content
  const shareData = {
    correctAnswers,
    totalQuestions,
    prize,
    cashedOut,
    walkedOut,
  };

  // Check if player completed all tiers (full game completion)
  const isFullCompletion = correctAnswers === totalQuestions && !walkedOut && !cashedOut;

  // Handle copying share link to clipboard
  const handleCopyShareLink = async () => {
    const success = await copyShareLinkToClipboard(shareData);
    if (success) {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 3000); // Reset after 3 seconds
    }
  };

  // Handle LinkedIn share
  const handleLinkedInShare = () => {
    // Get the pre-formatted LinkedIn message for user reference
    const linkedInMessage = generateLinkedInMessage(shareData);
    
    // Copy the message to clipboard for easy pasting
    navigator.clipboard.writeText(linkedInMessage).catch(() => {
      console.log('Could not copy LinkedIn message to clipboard');
    });
    
    // Open LinkedIn share dialog
    openLinkedInShare(shareData);
  };

  // Play 7 crore celebration audio on full completion
  useEffect(() => {
    if (isFullCompletion && !audioMuted) {
      const audio = new Audio('/audio/7_crore_meme_sound_kbc.mp3');
      // FAILSAFE: Set volume based on audio state
      audio.volume = audioMuted ? 0 : 0.6;
      audio.play().catch(() => {
        console.debug('7 crore celebration audio not available');
      });

      return () => {
        audio.pause();
        audio.currentTime = 0;
      };
    }
  }, [isFullCompletion, audioMuted]);

  const handleWaitlistSubmit = async () => {
    if (!userEmail) {
      setWaitlistError('Unable to get user email. Please try again.');
      return;
    }
    
    setWaitlistLoading(true);
    setWaitlistError(null);
    
    try {
      const result = await joinWaitlist(
        userEmail,
        displayName,
        'kbc_quiz',
        {
          quiz_score: correctAnswers,
          prize_won: prize?.label,
          prize_level: prize?.level,
          completed_at: new Date().toISOString()
        }
      );
      
      console.log('Waitlist signup result:', result);
      
      if (result.status === 'success' || result.status === 'already_exists') {
        setWaitlistSubmitted(true);
        
        // Keep the success message showing (don't auto-reset)
      } else {
        setWaitlistError('Failed to join waitlist. Please try again.');
      }
    } catch (error) {
      console.error('Error joining waitlist:', error);
      setWaitlistError('Failed to join waitlist. Please try again.');
    } finally {
      setWaitlistLoading(false);
    }
  };

  const getResultTitle = () => {
    if (walkedOut) return '🚪 You Walked Out';
    if (cashedOut) return '💰 Smart Move!';
    if (correctAnswers === totalQuestions) return '🏆 Perfect Score!';
    if (correctAnswers >= totalQuestions * 0.7) return '🎉 Great Job!';
    return '👍 Nice Try!';
  };

  const getResultMessage = () => {
    if (walkedOut) return 'You chose to quit mid-game. No prize was awarded. Better luck next time!';
    if (cashedOut) return 'You played it safe and secured your prize!';
    if (correctAnswers === totalQuestions) return 'You answered all questions correctly! You\'re a CoCaptain champion!';
    if (correctAnswers >= totalQuestions * 0.7) return 'You showed impressive knowledge of GenAI and CoCaptain!';
    return 'Keep learning and try again - you\'ll do better next time!';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-kbc-dark via-kbc-dark-accent to-kbc-dark p-4 overflow-y-auto relative">
      {/* User Profile - Top Left (Small) */}
      <KBCUserProfile size="small" position="top-left" />
      
      <div className="max-w-4xl mx-auto py-8">
        {/* Header */}
        <div className="text-center mb-8 animate-scale-in">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-2">
            {getResultTitle()}
          </h1>
          <p className="text-lg md:text-xl text-white/90">
            {getResultMessage()}
          </p>
        </div>

        {/* Results Card */}
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 md:p-8 border border-white/20 shadow-2xl mb-8">
          {/* Questions Correct */}
          <div className="text-center mb-6">
            <p className="text-white/80 text-sm mb-2">Questions Answered Correctly</p>
            <p className="text-5xl md:text-6xl font-bold text-kbc-gold">
              {correctAnswers} / {totalQuestions}
            </p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 gap-4 mb-6">
            <div className="bg-white/10 rounded-lg p-4 text-center">
              <p className="text-white/70 text-sm mb-1">Correct Answers</p>
              <p className="text-2xl font-bold text-kbc-success">
                {correctAnswers} / {totalQuestions}
              </p>
            </div>
          </div>

          {/* Prize Won */}
          {prize && !walkedOut && (
            <div className="bg-gradient-to-r from-kbc-gold/20 to-kbc-amber/20 border-2 border-kbc-gold rounded-lg p-6 mb-6">
              <p className="text-center text-white/80 text-sm mb-2">
                {cashedOut ? '🎁 Prize Claimed' : '🏆 Prize Won'}
              </p>
              <p className="text-center text-kbc-gold text-sm font-semibold mb-1">
                Tier {prize.level}
              </p>
              <p className="text-center text-2xl md:text-3xl font-bold text-white">
                {prize.label}
              </p>
            </div>
          )}

          {/* Walk Out Message */}
          {walkedOut && (
            <div className="bg-gradient-to-r from-kbc-error/20 to-red-600/20 border-2 border-kbc-error rounded-lg p-6 mb-6">
              <p className="text-center text-white/80 text-sm mb-2">
                ⚠️ No Prize Awarded
              </p>
              <p className="text-center text-lg text-white">
                You walked out before completing the quiz
              </p>
            </div>
          )}

          {/* Leaderboard Preview */}
          {leaderboardPreview.length > 0 && (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-white">
                  🏆 Top Players
                </h3>
                <span className="bg-kbc-success/20 text-kbc-success px-3 py-1 rounded-full text-sm font-semibold animate-pulse">
                  Live
                </span>
              </div>
              <div className="space-y-2">
                {leaderboardPreview.slice(0, 5).map((entry) => (
                  <div
                    key={entry.userId}
                    className="flex items-center justify-between bg-white/5 rounded-lg p-3 border border-white/10"
                  >
                    <div className="flex items-center">
                      <span className="text-kbc-gold font-bold mr-3 text-lg">
                        {getOrdinalSuffix(entry.rank)}
                      </span>
                      <span className="text-white font-medium">
                        {entry.displayName}
                      </span>
                    </div>
                    <div className="text-right">
                      <p className="text-kbc-amber font-bold">
                        {entry.questionsCorrect} correct
                      </p>
                      {entry.prizeWon && (
                        <p className="text-white/60 text-xs">
                          Tier {entry.prizeLevel}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <button
                onClick={onViewFullLeaderboard}
                className="w-full mt-3 text-white/80 hover:text-white font-medium py-2 text-sm underline"
              >
                View Full Leaderboard →
              </button>
            </div>
          )}
        </div>

        {/* Waiting List Signup */}
        {!waitlistSubmitted ? (
          <div className="text-center mb-8">
            <p className="text-white/70 text-sm mb-3">
              📬 Want updates on Shipment CoCaptain features?
            </p>
            <button
              onClick={handleWaitlistSubmit}
              disabled={waitlistLoading}
              className="bg-white/10 hover:bg-white/20 text-white font-medium text-sm py-2 px-6 rounded-lg border border-white/30 transition hover:border-white/50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {waitlistLoading ? 'Joining...' : 'Join Waitlist'}
            </button>
            {waitlistError && (
              <p className="text-kbc-error text-xs mt-2">
                {waitlistError}
              </p>
            )}
          </div>
        ) : (
          <div className="text-center mb-8 animate-scale-in">
            <p className="text-kbc-success text-sm font-semibold">
              ✓ Thanks! We'll keep you posted!
            </p>
          </div>
        )}

        {/* Attempt Notice & Actions */}
        <div className="space-y-4">
          <div className="w-full bg-white/5 text-white/80 font-medium text-sm py-4 px-6 rounded-lg border border-white/10 text-center">
            This result is final. You have up to 3 attempts per Maersk account.
          </div>
          <button
            onClick={onViewFullLeaderboard}
            className="w-full bg-gradient-to-r from-kbc-amber to-kbc-gold hover:from-kbc-gold hover:to-kbc-amber text-maersk-navy font-semibold py-3 rounded-lg border-2 border-kbc-gold/50 transition shadow-lg"
          >
            🏆 View Full Leaderboard
          </button>
          <button
            onClick={onBackToHome}
            className="w-full bg-white/10 hover:bg-white/20 text-white font-semibold py-3 rounded-lg border border-white/30 transition hover:border-white/50"
          >
            ← Back to KBC Home
          </button>
        </div>

        {/* Share Section */}
        <div className="mt-8 text-center">
          <p className="text-white/70 text-sm mb-3">Share your achievement:</p>
          <div className="flex flex-col sm:flex-row justify-center gap-3">
            {/* Copy Link Button */}
            <button 
              onClick={handleCopyShareLink}
              className="bg-white/10 hover:bg-white/20 text-white px-6 py-3 rounded-lg transition flex items-center justify-center gap-2 font-medium"
            >
              {copySuccess ? (
                <>
                  <FaCheck className="w-5 h-5" />
                  <span>Link Copied!</span>
                </>
              ) : (
                <>
                  <FaLink className="w-5 h-5" />
                  <span>Copy Share Link</span>
                </>
              )}
            </button>

            {/* LinkedIn Share Button */}
            <button 
              onClick={handleLinkedInShare}
              className="bg-[#0077B5] hover:bg-[#006399] text-white px-6 py-3 rounded-lg transition flex items-center justify-center gap-2 font-medium shadow-lg"
              title="Share on LinkedIn with #ShipmentCoCaptainTDD"
            >
              <FaLinkedin className="w-5 h-5" />
              <span>Share on LinkedIn</span>
            </button>
          </div>
          
          {/* LinkedIn Share Info */}
          <p className="text-white/50 text-xs mt-3">
            💡 Make sure to include <span className="text-kbc-gold">#ShipmentCoCaptainTDD</span> in your Linkedin share to stand a chance to win goodies!
          </p>
        </div>
      </div>
    </div>
  );
};

export default EndScreen;
